import React, { useState } from 'react';
import { Film, Tv, Search, Star, Play, Grid, List as ListIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import usePageTitle from '../hooks/usePageTitle';

const MediaCard = ({ icon: Icon, title, description, rating, year, category, image, episodes }) => (
  <div className="glow-card bg-dark-card border border-dark-border rounded-xl overflow-hidden hover:border-dark-border-light transition-all duration-400 cursor-pointer group">
    <div className="relative bg-dark-surface aspect-[2/3] flex items-center justify-center overflow-hidden border-b border-dark-border">
      <div className="text-6xl opacity-80">{image || '🎬'}</div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="absolute bottom-3 left-3 right-3">
          <div className="flex items-center justify-between text-white text-xs mb-2">
            <div className="flex items-center"><Star className="h-3 w-3 text-amber-400 fill-amber-400 mr-1" /><span className="font-bold">{rating}</span></div>
            <span className="bg-black/50 px-2 py-1 rounded">{year}</span>
          </div>
          <button className="w-full bg-white/90 hover:bg-white text-dark py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center">
            <Play className="h-4 w-4 mr-1" />立即观看
          </button>
        </div>
      </div>
      {episodes && <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">{episodes}集</div>}
      <div className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">{category}</div>
    </div>
    <div className="p-4">
      <h3 className="text-base font-semibold text-white group-hover:text-accent transition-colors line-clamp-2 mb-1">{title}</h3>
      <p className="text-sm text-gray-500 line-clamp-2 min-h-[2.5rem]">{description}</p>
    </div>
  </div>
);

const Media = () => {
  const { t } = useLanguage();
  usePageTitle('Media');
  const [selectedCategory, setSelectedCategory] = useState('全部');
  const [viewMode, setViewMode] = useState('grid');

  const categories = [
    '全部',
    '电影',
    '电视剧',
    '综艺',
    '纪录片',
    '动漫',
  ];

  const mediaItems = [
    {
      icon: Film,
      title: '年会不能停！',
      description: '职场喜剧，讲述打工人的真实故事，笑中带泪。',
      rating: '8.5',
      year: '2024',
      category: '电影',
      image: '🎬',
      episodes: null
    },
    {
      icon: Tv,
      title: '繁花',
      description: '王家卫执导的上海风情剧，展现90年代上海的繁华与梦想。',
      rating: '9.2',
      year: '2023',
      category: '电视剧',
      image: '📺',
      episodes: '30'
    },
    {
      icon: Film,
      title: '第二十条',
      description: '张艺谋执导的现实主义题材电影，聚焦司法正义。',
      rating: '8.3',
      year: '2024',
      category: '电影',
      image: '🎬',
      episodes: null
    },
    {
      icon: Tv,
      title: '漫长的季节',
      description: '悬疑剧，讲述跨越二十年的谜案，人物刻画深刻。',
      rating: '9.4',
      year: '2023',
      category: '电视剧',
      image: '📺',
      episodes: '12'
    },
    {
      icon: Film,
      title: '满江红',
      description: '张艺谋执导的古装悬疑喜剧，精彩的历史故事。',
      rating: '8.7',
      year: '2023',
      category: '电影',
      image: '🎬',
      episodes: null
    },
    {
      icon: Tv,
      title: '狂飙',
      description: '反腐题材剧，展现正义与邪恶的较量，紧张刺激。',
      rating: '9.1',
      year: '2023',
      category: '电视剧',
      image: '📺',
      episodes: '39'
    },
    {
      icon: Film,
      title: '热辣滚烫',
      description: '贾玲执导的励志喜剧，讲述女性成长的故事。',
      rating: '8.0',
      year: '2024',
      category: '电影',
      image: '🎬',
      episodes: null
    },
    {
      icon: Tv,
      title: '三体',
      description: '科幻剧，改编自刘慈欣经典小说，视觉震撼。',
      rating: '8.8',
      year: '2023',
      category: '电视剧',
      image: '📺',
      episodes: '30'
    },
    {
      icon: Film,
      title: '消失的她',
      description: '悬疑犯罪片，层层反转的精彩故事。',
      rating: '8.4',
      year: '2023',
      category: '电影',
      image: '🎬',
      episodes: null
    },
    {
      icon: Tv,
      title: '去有风的地方',
      description: '治愈系田园剧，讲述都市人寻找自我的故事。',
      rating: '8.6',
      year: '2023',
      category: '电视剧',
      image: '📺',
      episodes: '40'
    },
    {
      icon: Film,
      title: '流浪地球2',
      description: '科幻大片，中国科幻电影的新高度，视觉震撼。',
      rating: '8.9',
      year: '2023',
      category: '电影',
      image: '🎬',
      episodes: null
    },
    {
      icon: Tv,
      title: '开端',
      description: '时间循环悬疑剧，紧张刺激的剧情设计。',
      rating: '9.0',
      year: '2022',
      category: '电视剧',
      image: '📺',
      episodes: '15'
    },
    {
      icon: Film,
      title: '人生大事',
      description: '温情电影，讲述殡葬师与小女孩的感人故事。',
      rating: '8.8',
      year: '2022',
      category: '电影',
      image: '🎬',
      episodes: null
    },
    {
      icon: Tv,
      title: '人世间',
      description: '年代剧，展现中国五十年变迁，情感真挚。',
      rating: '9.3',
      year: '2022',
      category: '电视剧',
      image: '📺',
      episodes: '58'
    },
    {
      icon: Film,
      title: '你好，李焕英',
      description: '温情喜剧，贾玲的导演处女作，笑中带泪。',
      rating: '8.6',
      year: '2021',
      category: '电影',
      image: '🎬',
      episodes: null
    },
    {
      icon: Tv,
      title: '山海情',
      description: '脱贫攻坚剧，展现真实的中国乡村故事。',
      rating: '9.5',
      year: '2021',
      category: '电视剧',
      image: '📺',
      episodes: '23'
    },
    {
      icon: Film,
      title: '唐人街探案3',
      description: '悬疑喜剧，延续系列风格，情节紧凑。',
      rating: '8.1',
      year: '2021',
      category: '电影',
      image: '🎬',
      episodes: null
    },
    {
      icon: Tv,
      title: '隐秘的角落',
      description: '悬疑剧，改编自紫金陈小说，紧张刺激。',
      rating: '9.2',
      year: '2020',
      category: '电视剧',
      image: '📺',
      episodes: '12'
    },
    {
      icon: Film,
      title: '我和我的家乡',
      description: '国庆献礼片，五个温暖的故事。',
      rating: '8.5',
      year: '2020',
      category: '电影',
      image: '🎬',
      episodes: null
    },
    {
      icon: Tv,
      title: '庆余年',
      description: '古装权谋剧，改编自猫腻小说，情节精彩。',
      rating: '9.0',
      year: '2019',
      category: '电视剧',
      image: '📺',
      episodes: '46'
    },
  ];

  const filteredMedia = selectedCategory === '全部' 
    ? mediaItems 
    : mediaItems.filter(item => item.category === selectedCategory);

  return (
    <div className="-mt-16">
      <section className="relative pt-36 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-mesh opacity-50" />
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-gradient-to-br from-[#4796E3]/12 via-[#4796E3]/08 to-transparent rounded-full blur-[100px]" />
          <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] bg-gradient-to-tr from-[#4796E3]/08 to-transparent rounded-full blur-[100px]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center px-3.5 py-1.5 text-[11px] font-semibold uppercase text-violet-400 bg-violet-500/10 border border-violet-500/20 rounded-full mb-6">{t('media.inDevelopment')}</span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white mb-4">{t('media.title')}</h1>
          <p className="text-lg md:text-xl text-gray-400 max-w-3xl mx-auto">最新的电影、电视剧和精彩内容，精选优质影视作品</p>
          <div className="mt-8 max-w-2xl mx-auto">
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none"><Search className="h-5 w-5 text-gray-500 group-focus-within:text-accent transition-colors" /></div>
              <input className="block w-full pl-12 pr-4 py-3 bg-dark-card border border-dark-border text-white placeholder-gray-500 rounded-xl focus:ring-2 focus:ring-accent/30 focus:border-accent/50 outline-none text-base" placeholder="搜索电影、电视剧..." type="search" />
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button key={category} onClick={() => setSelectedCategory(category)} className={`px-4 py-2 rounded-lg text-[13px] font-medium transition-all duration-300 ${selectedCategory === category ? 'bg-accent/15 text-accent border border-accent/25' : 'bg-dark-surface text-gray-400 border border-dark-border hover:text-white hover:border-dark-border-light'}`}>
                  {category}
                </button>
              ))}
            </div>
            <div className="hidden md:flex items-center gap-2">
              <button onClick={() => setViewMode('grid')} className={`p-2 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-accent/15 text-accent border border-accent/25' : 'bg-dark-surface text-gray-400 border border-dark-border hover:text-white'}`}><Grid className="h-5 w-5" /></button>
              <button onClick={() => setViewMode('list')} className={`p-2 rounded-lg transition-all ${viewMode === 'list' ? 'bg-accent/15 text-accent border border-accent/25' : 'bg-dark-surface text-gray-400 border border-dark-border hover:text-white'}`}><ListIcon className="h-5 w-5" /></button>
            </div>
          </div>
          <div className="mb-6 flex items-center justify-between">
            <p className="text-sm text-gray-400">共找到 <span className="font-bold text-accent">{filteredMedia.length}</span> 部作品</p>
            <select className="border border-dark-border rounded-xl px-3 py-1.5 text-sm focus:ring-2 focus:ring-accent/30 outline-none bg-dark-card text-white">
              <option>最新上线</option>
              <option>评分最高</option>
              <option>年份最新</option>
              <option>最受欢迎</option>
            </select>
          </div>
          <div className={viewMode === 'grid' ? 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4' : 'space-y-4'}>
            {filteredMedia.map((item, index) => <MediaCard key={index} {...item} />)}
          </div>
          <div className="mt-8 flex justify-center items-center space-x-2">
            <button className="px-4 py-2 border border-dark-border rounded-xl text-sm text-gray-400 hover:bg-dark-surface bg-dark-card disabled:opacity-30" disabled>上一页</button>
            <button className="px-4 py-2 bg-accent/15 text-accent rounded-xl text-sm font-medium border border-accent/25">1</button>
            <button className="px-4 py-2 border border-dark-border rounded-xl text-sm text-gray-400 hover:bg-dark-surface bg-dark-card">2</button>
            <button className="px-4 py-2 border border-dark-border rounded-xl text-sm text-gray-400 hover:bg-dark-surface bg-dark-card">3</button>
            <button className="px-4 py-2 border border-dark-border rounded-xl text-sm text-gray-400 hover:bg-dark-surface bg-dark-card">下一页</button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Media;
